import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { ApiError, api } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import type { CallListParams, SalesConfig } from "../../lib/sales-types";
import type { User } from "../../types";
import { CallRow } from "../../components/sales/CallRow";
import { Button } from "../../components/ui/Button";
import { Select } from "../../components/ui/Select";
import { cn } from "../../components/ui/cn";

/**
 * The worklist, and every slice of it.
 *
 * One screen rather than five, because "my calls", "the pool", "the overdue
 * ones" and "closed calls" are the same list with a filter on it -- and five
 * pages would be five places for the row layout to drift apart. The filters
 * live in the URL so a slice is a link: "have a look at the Shelly ones nobody
 * has picked up" is something you paste, not something you explain.
 *
 * The ordering is the server's. That is worth stating plainly because it is
 * unusual for a list this interactive: what to ring next depends on
 * manager-configured thresholds per group, so it is decided once in
 * `worklist.ts` and never recomputed here. A front end that re-sorted would be
 * a second implementation of the one rule this product exists to get right.
 */
const PAGE_SIZE = 50;

export function CallsList({
  user,
  /** Fixed to closed calls, for the "Closed calls" screen the brief asks for. */
  closed,
}: {
  user: User;
  closed?: boolean;
}) {
  const [params, setParams] = useSearchParams();
  const client = useQueryClient();

  const mine = params.get("mine") === "true";
  const unassigned = params.get("unassigned") === "true";

  const query: CallListParams = {
    status: closed ? "closed" : "open",
    groupId: params.get("group") ? Number(params.get("group")) : undefined,
    /* "Mine" is resolved here rather than being a server-side keyword, so the
       same endpoint serves a manager looking at somebody else's list. */
    assigneeUserId: mine
      ? user.id
      : params.get("assignee")
        ? Number(params.get("assignee"))
        : undefined,
    unassigned: unassigned || undefined,
    overdueOnly: params.get("overdue") === "true" || undefined,
    q: params.get("q") ?? undefined,
    from: params.get("from") ?? undefined,
    to: params.get("to") ?? undefined,
    limit: PAGE_SIZE,
    offset: Number(params.get("offset") ?? 0),
  };

  const config = useQuery({
    queryKey: queryKeys.salesConfig,
    queryFn: salesApi.config,
    staleTime: 5 * 60_000,
  });

  const users = useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    staleTime: 5 * 60_000,
  });

  const calls = useQuery({
    queryKey: queryKeys.salesCalls(query as Record<string, unknown>),
    queryFn: () => salesApi.calls(query),
    /*
     * Short. Somebody else claiming a call is the change that matters most on
     * this screen, and fifteen seconds of staleness is the difference between
     * a wasted dial and a clean handover.
     */
    staleTime: 15_000,
  });

  const claim = useMutation({
    mutationFn: (id: number) => salesApi.claim(id),
    onSettled: () => client.invalidateQueries({ queryKey: queryKeys.salesRoot }),
  });

  /* Reordering the queue is a manager's, matching the route. Undefined for
     everybody else, which is what hides the control on the row. */
  const pin = useMutation({
    mutationFn: ({ id, pinned }: { id: number; pinned: boolean }) =>
      salesApi.pin(id, pinned),
    onSettled: () => client.invalidateQueries({ queryKey: queryKeys.salesRoot }),
  });

  /**
   * Apply several filter changes as one navigation.
   *
   * Deliberately not a single-key setter called three times: each call would
   * build its `URLSearchParams` from the same render's `params`, so the last
   * one would win and quietly discard the other two. Picking "Mine" has to
   * clear "In the pool" *and* the assignee dropdown in the same step.
   */
  const set = (patch: Record<string, string | null>): void => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    /* Any filter change resets paging. Landing on page three of a list that
       now has four rows is a blank screen with no explanation. */
    if (!("offset" in patch)) next.delete("offset");
    setParams(next, { replace: true });
  };

  const offset = Number(params.get("offset") ?? 0);
  const total = calls.data?.total ?? 0;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <Filters
        params={params}
        set={set}
        config={config.data}
        users={users.data}
        mine={mine}
        unassigned={unassigned}
        closed={closed}
        userId={user.id}
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
        {claim.isError ? (
          /*
            The collision message, and the one piece of UI the whole claiming
            design exists for. It names who got there first, because "could not
            claim" leaves somebody wondering whether to try again.
          */
          <p className="border-b border-line bg-red-50 px-4 py-2 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-300">
            {claim.error instanceof ApiError
              ? claim.error.message
              : "Could not claim that call."}
          </p>
        ) : null}

        {calls.isLoading ? (
          <p className="p-8 text-center text-sm text-faint">Loading…</p>
        ) : total === 0 ? (
          <p className="p-8 text-center text-sm text-faint">
            {closed
              ? "No calls have been closed in this range."
              : "Nothing here. Either the desk is on top of it, or the filters are too narrow."}
          </p>
        ) : (
          <>
            {calls.data!.truncated ? (
              <p className="border-b border-line bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                There are more open calls than this list ranks at once. The
                oldest are shown; narrow the filters to see the rest in order.
              </p>
            ) : null}

            {calls.data!.calls.map((call) => (
              <CallRow
                key={call.id}
                call={call}
                currentUserId={user.id}
                onClaim={closed ? undefined : claim.mutate}
                /* Not on the closed list: pinning orders work still to do. */
                onPin={
                  closed || user.role !== "admin"
                    ? undefined
                    : (id, pinned) => pin.mutate({ id, pinned })
                }
                claiming={claim.isPending && claim.variables === call.id}
              />
            ))}
          </>
        )}
      </div>

      <footer className="flex items-center gap-3 border-t border-line px-4 py-2 text-xs text-faint">
        <span>
          {total === 0
            ? "No calls"
            : `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total}`}
        </span>
        <span className="ml-auto flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={offset === 0}
            onClick={() => set({ offset: String(Math.max(0, offset - PAGE_SIZE)) })}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => set({ offset: String(offset + PAGE_SIZE) })}
          >
            Next
          </Button>
        </span>
      </footer>
    </div>
  );
}

/**
 * The filter bar.
 *
 * Every filter the brief lists, and no more. "Mine" and "In the pool" are
 * toggle buttons rather than options in the assignee dropdown, because they are
 * the two anybody actually uses and burying them under a select would make the
 * common case three clicks.
 *
 * On a phone it is laid out rather than left to wrap: search on a line of its
 * own, the two selects halving the next, the toggles, then the date range
 * across the width. Wrapped as it came, the search box was cut to "Company,
 * subject or nu", the selects sat at whatever width their options gave them,
 * and the second date ran into the screen's edge.
 */
function Filters({
  params,
  set,
  config,
  users,
  mine,
  unassigned,
  closed,
  userId,
}: {
  params: URLSearchParams;
  set: (patch: Record<string, string | null>) => void;
  config: SalesConfig | undefined;
  users: User[] | undefined;
  mine: boolean;
  unassigned: boolean;
  closed?: boolean;
  userId: number;
}) {
  const active = <T extends { isActive: boolean }>(rows: T[] | undefined) =>
    (rows ?? []).filter((row) => row.isActive);

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2 sm:px-4">
      <input
        value={params.get("q") ?? ""}
        onChange={(event) => set({ q: event.target.value })}
        placeholder="Company, subject or number"
        className="h-8 w-full rounded-md border border-line bg-surface px-2 text-xs sm:h-7 sm:w-48 text-content placeholder:text-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
      />

      {/* Groups only. The separate priority tier is gone -- this desk's
          groups are its tiers. */}
      <Select
        value={params.get("group") ?? ""}
        onChange={(value) => set({ group: value })}
        label="Call group"
        className="h-8 w-[calc(50%-0.25rem)] sm:h-7 sm:w-auto"
        options={[
          { value: "", label: "Any group" },
          ...active(config?.groups).map((group) => ({
            value: String(group.id),
            label: group.name,
          })),
        ]}
      />

      <Select
        value={mine ? String(userId) : (params.get("assignee") ?? "")}
        onChange={(value) =>
          set({ mine: null, unassigned: null, assignee: value || null })
        }
        label="Assignee"
        className="h-8 w-[calc(50%-0.25rem)] sm:h-7 sm:w-auto"
        options={[
          { value: "", label: "Anybody" },
          ...(users ?? []).map((person) => ({
            value: String(person.id),
            label: person.name,
          })),
        ]}
      />

      <Toggle
        on={mine}
        onClick={() =>
          set({ unassigned: null, assignee: null, mine: mine ? null : "true" })
        }
      >
        Mine
      </Toggle>

      {!closed ? (
        <>
          <Toggle
            on={unassigned}
            onClick={() =>
              set({
                mine: null,
                assignee: null,
                unassigned: unassigned ? null : "true",
              })
            }
          >
            In the pool
          </Toggle>

          <Toggle
            on={params.get("overdue") === "true"}
            onClick={() =>
              set({
                overdue: params.get("overdue") === "true" ? null : "true",
              })
            }
          >
            Needs attention
          </Toggle>
        </>
      ) : null}

      <span className="flex w-full items-center gap-1 text-[11px] text-faint sm:ml-auto sm:w-auto">
        {/*
          The range means "raised" on the open list and "closed" on the closed
          one -- in each case the date somebody has in mind when they narrow
          that particular list. Labelled, because guessing is worse.
        */}
        {closed ? "Closed" : "Raised"}
        <input
          type="date"
          value={params.get("from") ?? ""}
          onChange={(event) => set({ from: event.target.value })}
          aria-label="From"
          className="h-8 min-w-0 flex-1 rounded-md border border-line bg-surface px-1.5 text-xs text-content sm:h-7 sm:flex-none"
        />
        <span>–</span>
        <input
          type="date"
          value={params.get("to") ?? ""}
          onChange={(event) => set({ to: event.target.value })}
          aria-label="To"
          className="h-8 min-w-0 flex-1 rounded-md border border-line bg-surface px-1.5 text-xs text-content sm:h-7 sm:flex-none"
        />
      </span>
    </div>
  );
}

function Toggle({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        "h-7 rounded-md border px-2 text-xs transition-colors",
        on
          ? "border-brand-600 bg-brand-600 font-medium text-white"
          : "border-line bg-surface text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
      )}
    >
      {children}
    </button>
  );
}
